import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Reveal from "../components/kedve/Reveal";
import { ACCELERATORS } from "../data/kedve";

const AUDIENCE = [
  { title: "Rising Professionals", body: "Individual contributors preparing for their first move into influence." },
  { title: "Managers", body: "First-time and second-time managers moving from doing to leading." },
  { title: "Senior Operators", body: "Directors and senior specialists preparing for executive rooms." },
  { title: "Founders & Consultants", body: "Advisors and operators who build trust with clients and boards." },
];

const HOW_YOU_LEARN = [
  { label: "Live rooms", body: "Nothing at kedve is recorded first. Every session is live and in dialogue." },
  { label: "Small cohorts", body: "Cohorts are capped at forty. You are known, and you are heard." },
  { label: "Rehearsals", body: "You practise the moment before you learn its theory. Feedback follows." },
  { label: "Applied work", body: "Each week returns to the work you already do. kedve travels with you." },
];

const CAREER_STORIES = [
  {
    name: "Priya S.",
    from: "Product Manager",
    to: "Head of Product",
    quote: "kedve rebuilt the way I show up in an executive room. Six months later, my CEO asked me to run product strategy.",
  },
  {
    name: "Marcus D.",
    from: "Senior Engineer",
    to: "Engineering Director",
    quote: "I stopped studying content and started rehearsing conversations. That is the entire difference.",
  },
];

export default function Home() {
  return (
    <div data-testid="page-home" className="text-black">
      {/* HERO */}
      <section className="relative flex flex-col overflow-hidden pt-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 w-full flex-1 flex flex-col justify-center pt-8 pb-12 min-h-[calc(78vh-4rem)]">
          <div className="eyebrow opacity-70 animate-fade-up">A Professional Growth Platform</div>

          <div className="mt-8 md:mt-10 grid md:grid-cols-12 gap-8 md:gap-12 items-start">
            <h1
              className="md:col-span-7 h-display text-[11vw] md:text-[8.4vw] lg:text-[7.4vw] leading-[0.92] animate-fade-up"
              data-testid="hero-h1"
            >
              Build the<br />
              capabilities<br />
              that define<br />
              <span className="italic">great careers.</span>
            </h1>

            <div
              className="md:col-span-4 md:col-start-9 flex flex-col justify-between h-full min-h-[220px] animate-fade-up"
              style={{ animationDelay: "150ms" }}
            >
              <p className="text-base md:text-lg leading-relaxed opacity-70 max-w-sm">
                kedve is a professional growth platform for ambitious professionals who want to communicate better, influence confidently and lead with impact.
              </p>

              <div className="mt-10 flex flex-col items-start gap-4">
                <div className="flex flex-wrap gap-3">
                  <Link to="/apply" data-testid="hero-cta-apply" className="inline-flex items-center gap-2 bg-black text-white rounded-full px-6 py-3.5 text-sm tracking-wide hover:bg-black/85 transition">
                    Apply Now <ArrowUpRight className="w-4 h-4" />
                  </Link>
                  <a href="#accelerators" data-testid="hero-cta-explore" className="inline-flex items-center gap-2 border border-black rounded-full px-6 py-3.5 text-sm tracking-wide hover:bg-black hover:text-white transition">
                    See the Accelerators
                  </a>
                </div>
                <div className="eyebrow opacity-40 pt-2">Applications open · Next cohort forming</div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full">
          <div className="relative w-full h-[42vh] md:h-[46vh] overflow-hidden bg-black">
            <img
              src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=2400&q=80"
              alt="Professionals in a working conversation"
              className="absolute inset-0 w-full h-full object-cover grayscale opacity-95"
              style={{ objectPosition: "center 40%" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
            <div className="absolute bottom-6 left-6 md:left-12 right-6 md:right-12 flex justify-between text-white eyebrow">
              <span>Professional Growth</span>
              <span>Executive Learning</span>
            </div>
          </div>
        </div>
      </section>

      {/* WHY KEDVE EXISTS */}
      <section className="bg-black text-white py-32 md:py-52" data-testid="why-kedve">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="eyebrow text-white/50">Why kedve exists</div>
          <Reveal>
            <p className="mt-8 font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-6xl">
              Most careers stall at the moment they should accelerate. Not because talent is missing. <span className="italic text-white/50">The capabilities that define great careers</span> are rarely taught.
            </p>
          </Reveal>
          <Reveal delay={150}>
            <p className="mt-16 text-lg text-white/60 max-w-lg">
              kedve is where those capabilities are built. Deliberately. In small rooms. With practitioners who have done the work.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CAREER ACCELERATORS — CENTERPIECE */}
      <section id="accelerators" className="py-28 md:py-44">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 mb-20 md:mb-28">
            <div className="md:col-span-7">
              <div className="eyebrow opacity-60">Career Accelerators</div>
              <h2 className="mt-6 font-serif text-5xl md:text-7xl leading-[0.95] tracking-tightest">
                Which accelerator<br /><span className="italic">is right for you?</span>
              </h2>
            </div>
            <p className="md:col-span-4 md:col-start-9 text-lg leading-relaxed opacity-70 self-end">
              Choose the accelerator that matches your current career stage and prepares you for what&apos;s next.
            </p>
          </div>

          <div data-testid="accelerators-list">
            {ACCELERATORS.map((a, i) => (
              <Reveal key={a.id} delay={i * 30}>
                <Link
                  to="/accelerators"
                  data-testid={`program-${a.id}`}
                  className="group grid grid-cols-12 gap-6 md:gap-12 py-10 md:py-14 items-start transition-colors cursor-pointer"
                >
                  {/* LEFT — title always visible */}
                  <div className="col-span-12 md:col-span-5">
                    <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tightest leading-[1.05] opacity-90 group-hover:opacity-100 transition-all duration-500 group-hover:italic">
                      {a.title}
                    </h3>
                  </div>

                  {/* RIGHT — outcome always visible, expanded panel appears on hover */}
                  <div className="col-span-12 md:col-span-7">
                    <p className="text-lg md:text-xl leading-relaxed opacity-75 max-w-2xl">
                      {a.outcome}
                    </p>

                    <div
                      className="grid transition-all duration-[600ms] ease-out mt-0 opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:mt-8"
                      style={{ gridTemplateRows: "0fr" }}
                      onMouseEnter={undefined}
                    >
                      <div className="overflow-hidden group-hover:[grid-template-rows:1fr]">
                        {/* fallback — content still layouted via mt padding */}
                      </div>
                    </div>

                    {/* Expanded content — sits below outcome, fades in */}
                    <div className="mt-6 md:mt-8 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 ease-out pointer-events-none group-hover:pointer-events-auto">
                      <div className="eyebrow opacity-50">Ideal for</div>
                      <p className="mt-3 text-base md:text-lg opacity-80 leading-relaxed">
                        {a.idealFor.map((who, idx) => (
                          <span key={who}>
                            {idx > 0 && <span className="opacity-30 mx-2.5">·</span>}
                            {who}
                          </span>
                        ))}
                      </p>

                      <div className="mt-6 flex flex-wrap gap-8">
                        <span className="inline-flex items-center gap-1.5 text-sm tracking-wide link-underline">
                          View Details <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-sm tracking-wide link-underline">
                          Apply Now <ArrowUpRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
                {/* whisper divider — barely there, only present between items */}
                {i < ACCELERATORS.length - 1 && (
                  <div className="h-px bg-black/[0.06]" />
                )}
              </Reveal>
            ))}
          </div>

          {/* GUIDANCE PANEL */}
          <div className="mt-24 md:mt-32 grid md:grid-cols-12 gap-10 items-end" data-testid="accelerator-guidance">
            <div className="md:col-span-7">
              <div className="eyebrow opacity-60">Guidance</div>
              <h3 className="mt-6 font-serif text-4xl md:text-6xl tracking-tightest leading-[0.95]">
                Not sure where <span className="italic">to begin?</span>
              </h3>
              <p className="mt-6 max-w-lg text-lg leading-relaxed opacity-70">
                Answer a few questions and we&apos;ll recommend the accelerator that best matches your current career stage.
              </p>
            </div>
            <div className="md:col-span-4 md:col-start-9 flex flex-col gap-3 md:items-end">
              <Link to="/apply" data-testid="find-accelerator" className="inline-flex items-center gap-2 bg-black text-white rounded-full px-7 py-4 text-sm tracking-wide hover:bg-black/85 transition">
                Find My Accelerator <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/accelerators" data-testid="cta-accelerators" className="inline-flex items-center gap-2 border border-black rounded-full px-7 py-4 text-sm tracking-wide hover:bg-black hover:text-white transition">
                Explore All Programs <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="bg-neutral-50 py-28 md:py-40" data-testid="who-its-for">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 mb-20">
            <div className="md:col-span-6">
              <div className="eyebrow opacity-60">Who it&apos;s for</div>
              <h2 className="mt-6 font-serif text-5xl md:text-6xl leading-[0.95] tracking-tightest">
                Built for<br /><span className="italic">professionals who intend to move.</span>
              </h2>
            </div>
            <p className="md:col-span-4 md:col-start-9 text-lg leading-relaxed opacity-70 self-end">
              kedve is not for everyone, and not intended to be. It is designed for a specific kind of professional at a specific moment in their career.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-12 gap-y-14">
            {AUDIENCE.map((a, i) => (
              <Reveal key={a.title} delay={i * 80}>
                <div data-testid={`audience-${i}`}>
                  <div className="w-6 h-px bg-black" />
                  <h3 className="mt-6 font-serif text-2xl md:text-3xl tracking-tightest leading-tight">{a.title}</h3>
                  <p className="mt-4 text-base leading-relaxed opacity-70 max-w-xs">{a.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW YOU LEARN */}
      <section className="py-28 md:py-40" data-testid="how-you-learn">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 mb-20">
            <div className="md:col-span-6">
              <div className="eyebrow opacity-60">How you learn</div>
              <h2 className="mt-6 font-serif text-5xl md:text-6xl leading-[0.95] tracking-tightest">
                Live, small,<br /><span className="italic">and rehearsed.</span>
              </h2>
            </div>
            <p className="md:col-span-4 md:col-start-9 text-lg leading-relaxed opacity-70 self-end">
              You do not watch kedve. You practise it, in the room, with people who have done the work.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14">
            {HOW_YOU_LEARN.map((step, i) => (
              <Reveal key={step.label} delay={i * 80}>
                <div className="flex gap-8 items-baseline" data-testid={`how-${i}`}>
                  <div className="font-mono text-xs opacity-40 flex-shrink-0 pt-3">— {String(i + 1).padStart(2, "0")}</div>
                  <div>
                    <h3 className="font-serif text-3xl md:text-4xl tracking-tightest leading-tight">{step.label}</h3>
                    <p className="mt-4 text-base md:text-lg leading-relaxed opacity-75 max-w-md">{step.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ENTERPRISE LEARNING */}
      <section className="bg-black text-white py-28 md:py-40" data-testid="enterprise-learning">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <div className="eyebrow text-white/50">Enterprise learning</div>
            <h2 className="mt-6 font-serif text-5xl md:text-6xl leading-[0.95] tracking-tightest">
              Capability, built<br /><span className="italic">into the business.</span>
            </h2>
            <p className="mt-8 max-w-lg text-white/70 leading-relaxed">
              kedve partners with organisations to develop leaders from within. Corporate academies, executive fellowships and role-specific programs, designed with your business, not for it.
            </p>
          </div>
          <div className="md:col-span-4 md:col-start-9 flex flex-col justify-end gap-4">
            <Link to="/enterprise" data-testid="cta-enterprise" className="inline-flex items-center justify-between border border-white/40 hover:border-white rounded-full px-7 py-4 text-sm tracking-wide transition">
              For Business <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CAREER STORIES */}
      <section className="py-28 md:py-40" data-testid="career-stories">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 mb-20">
            <div className="md:col-span-6">
              <div className="eyebrow opacity-60">Career stories</div>
              <h2 className="mt-6 font-serif text-5xl md:text-6xl leading-[0.95] tracking-tightest">
                The move,<br /><span className="italic">in their words.</span>
              </h2>
            </div>
            <p className="md:col-span-4 md:col-start-9 text-lg leading-relaxed opacity-70 self-end">
              A few of the people this was built for. Six months later, most careers looked different.
            </p>
          </div>

          <div className="space-y-20 md:space-y-24">
            {CAREER_STORIES.map((s, i) => (
              <Reveal key={s.name} delay={i * 80}>
                <div className="grid md:grid-cols-12 gap-8" data-testid={`story-${i}`}>
                  <div className="md:col-span-3">
                    <div className="font-mono text-xs opacity-60">— 0{i + 1}</div>
                    <div className="mt-4 font-serif text-2xl tracking-tight">{s.name}</div>
                    <div className="mt-2 text-sm opacity-70">
                      {s.from} <span className="italic opacity-50">to</span> {s.to}
                    </div>
                  </div>
                  <blockquote className="md:col-span-9 font-serif text-3xl md:text-5xl leading-[1.15] tracking-tightest italic opacity-90">
                    &ldquo;{s.quote}&rdquo;
                  </blockquote>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-16">
            <Link to="/philosophy#stories" className="inline-flex items-center gap-2 text-sm tracking-wide opacity-70 hover:opacity-100 link-underline">
              Read more career stories <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-black text-white py-32 md:py-52">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-6xl md:text-8xl lg:text-9xl leading-[0.9] tracking-tightest">
            Choose your<br /><span className="italic">accelerator.</span>
          </h2>
          <p className="mt-10 max-w-xl mx-auto text-white/70 leading-relaxed">
            The next kedve cohort begins soon. Applications are read by our team, not by a machine.
          </p>
          <div className="mt-12 flex flex-wrap gap-4 justify-center">
            <Link to="/apply" data-testid="closing-cta-apply" className="inline-flex items-center gap-2 bg-white text-black rounded-full px-8 py-4 text-sm tracking-wide hover:bg-white/90 transition">
              Begin your application <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a href="#accelerators" className="inline-flex items-center gap-2 border border-white/40 hover:border-white rounded-full px-8 py-4 text-sm tracking-wide transition">
              See the programs
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
